/**
 * WeatherLayer — DYNAMIC procedural weather. The previous version was a
 * permanent always-on rainstorm that overpowered the screen; this rewrite
 * runs a small state machine so the world is mostly CLEAR with occasional
 * drift through overcast / light rain / storm.
 *
 * State machine:
 *   clear      — no rain, baseline ambient wisps only. Default & most common.
 *   overcast   — very faint mist, barely visible. The "something might be
 *                coming" mood without committing to actual rain.
 *   light_rain — visible drizzle. Still subtle; gameplay reads first.
 *   storm      — heaviest state. Rain + occasional lightning. Rare.
 *
 * Each state has a target INTENSITY (0..1). The layer lerps from current
 * intensity toward the target over `TRANSITION_MS` so transitions are
 * smooth, never jarring. Rain alpha / wisp alpha / lightning eligibility
 * all scale off the live intensity.
 *
 * Initial-state distribution is heavily biased toward clear so a fresh
 * match almost always starts dry. Storms are rare events the player will
 * notice and remember; if every match is a storm the dramatic moments stop
 * feeling dramatic.
 *
 * Pure presentation: no game-state reads, no writes to MatchState. Call
 *   new WeatherLayer(scene, map) in GameScene.create(),
 *   weather.update(timeMs)   in GameScene.update(),
 *   weather.dispose()        in the SHUTDOWN handler.
 */

import type { MapDef } from '@signalclash/shared';
import Phaser from 'phaser';

// ---------------------------------------------------------------------------
// State machine tuning
// ---------------------------------------------------------------------------

type WeatherState = 'clear' | 'overcast' | 'light_rain' | 'storm';

// Target intensity per state (0 = no rain, 1 = peak storm).
const STATE_INTENSITY: Record<WeatherState, number> = {
  clear: 0.0,
  overcast: 0.18,
  light_rain: 0.55,
  storm: 1.0,
};

// Smooth lerp duration when transitioning between states (ms).
const TRANSITION_MS = 6_000;

// Dwell time per state (ms). Storm dwells SHORT so it doesn't dominate.
// Clear dwells LONG so it's the default mood.
const DWELL_RANGES: Record<WeatherState, [number, number]> = {
  clear: [90_000, 180_000],      // 1.5 – 3 minutes of nothing
  overcast: [45_000, 90_000],    // 45 s – 1.5 minutes
  light_rain: [30_000, 60_000],  // 30 s – 1 minute
  storm: [15_000, 30_000],       // 15 – 30 s (rare, dramatic)
};

// Transition table. Each state lists candidate next-states with weights.
// Bias is HEAVILY toward returning to clear so the world is mostly dry.
// Storms must enter through light_rain (no clear → storm jumps).
const TRANSITIONS: Record<WeatherState, Array<[WeatherState, number]>> = {
  clear:      [['clear', 4], ['overcast', 3]],
  overcast:   [['clear', 5], ['light_rain', 2], ['overcast', 1]],
  light_rain: [['overcast', 4], ['storm', 1], ['clear', 2]],
  storm:      [['light_rain', 6], ['overcast', 1]],
};

// ---------------------------------------------------------------------------
// Rain tuning — peak (storm-intensity) values. Live alpha = peak × intensity.
// Reduced from [0.32, 0.22, 0.14] in the always-on version so even storm
// peak is gentler than the old "constant" reading.
// ---------------------------------------------------------------------------

const RAIN_TEX_KEY   = 'weather_rain';
const RAIN_LAYERS    = 3;
const RAIN_SPEEDS    = [340, 220, 140];    // px/s per layer
const RAIN_X_DRIFT   = [0.22, 0.14, 0.08]; // px/ms horizontal drift
const RAIN_SCALES    = [1.0, 1.4, 1.8];    // tile scale (far = larger tile)
const RAIN_PEAK_ALPHAS = [0.18, 0.12, 0.07]; // ↓ from [0.32, 0.22, 0.14]

const RAIN_STREAK_COUNT  = 72;
const RAIN_ANGLE_RAD     = 0.26;            // ~15° diagonal slant

// ---------------------------------------------------------------------------
// Lightning tuning — only fires during storm AND when storm intensity is
// near its target (i.e., not while transitioning in/out). Interval slightly
// wider than the old version so storms feel like storms, not strobes.
// ---------------------------------------------------------------------------

const LIGHTNING_INTERVAL_MIN_MS = 6_000;
const LIGHTNING_INTERVAL_MAX_MS = 14_000;
const LIGHTNING_FLASH_ALPHA     = 0.20;   // peak overlay alpha
const LIGHTNING_FADE_MS         = 180;
// Intensity threshold below which lightning is suppressed. The storm has
// to be at least 80% of its target intensity before any flash fires, so
// transitions into/out of storm are silent.
const LIGHTNING_INTENSITY_GATE  = 0.80;

// ---------------------------------------------------------------------------
// Wisp tuning — ambient electrical motes. Always faintly present so the
// world isn't dead even in clear weather, but scaled up during rain.
// ---------------------------------------------------------------------------

const WISP_COUNT             = 14;        // ↓ from 18
const WISP_BASE_ALPHA        = 0.05;      // always-on minimum
const WISP_INTENSITY_BONUS   = 0.07;      // additional alpha at storm peak
const FX_KEY                 = 'fx';

interface RainLayer {
  sprite: Phaser.GameObjects.TileSprite;
  speedY: number;     // px/ms
  driftX: number;     // px/ms
  peakAlpha: number;  // alpha at intensity = 1
}

interface Wisp {
  img: Phaser.GameObjects.Image;
  vx: number; vy: number;
  phaseMs: number;
  pulseHz: number;
  ww: number; wh: number;
}

export class WeatherLayer {
  private readonly layers: RainLayer[] = [];
  private readonly wisps: Wisp[] = [];
  private lightning: Phaser.GameObjects.Graphics | null = null;
  private lightningBornMs = 0;
  private lightningActive = false;
  private nextLightningMs = 0;
  private lastUpdateMs: number | null = null;
  private readonly allObjs: Phaser.GameObjects.GameObject[] = [];

  // Weather state machine.
  private state: WeatherState = 'clear';
  private intensity = 0;                  // current rain intensity 0..1
  private targetIntensity = 0;            // where we're lerping toward
  private stateChangedAtMs = 0;           // last transition wall-clock
  private nextStateAtMs = 0;              // when to roll the dice again

  constructor(private readonly scene: Phaser.Scene, map: MapDef) {
    this.buildRainTexture();
    this.buildRainLayers(map);
    this.buildLightningFlash(map);
    this.buildWisps(map);

    // First match seconds are quiet — fresh match almost always starts clear.
    // A 1-in-8 chance starts overcast (mood without rain); never starts in
    // rain or storm so the player isn't dropped into a downpour at 0:00.
    this.state = Math.random() < 0.125 ? 'overcast' : 'clear';
    this.targetIntensity = STATE_INTENSITY[this.state];
    this.intensity = this.targetIntensity;  // no startup lerp
    this.stateChangedAtMs = 0;
    this.nextStateAtMs = this.rollDwell(this.state, 0);
    this.nextLightningMs = Number.POSITIVE_INFINITY; // gated by storm state
  }

  dispose(): void {
    for (const o of this.allObjs) o.destroy();
    this.allObjs.length = 0;
    this.layers.length = 0;
    this.wisps.length = 0;
    this.lightning = null;
  }

  /** Test/dev seam — read current state for telemetry or HUD overlays. */
  getState(): WeatherState {
    return this.state;
  }

  /** Test/dev seam — read current rain intensity 0..1. */
  getIntensity(): number {
    return this.intensity;
  }

  update(nowMs: number): void {
    const dt = this.lastUpdateMs === null ? 0 : Math.min(50, nowMs - this.lastUpdateMs);
    this.lastUpdateMs = nowMs;

    // Roll a new state when the current dwell expires.
    if (nowMs >= this.nextStateAtMs) {
      this.advanceState(nowMs);
    }

    // Smooth lerp toward target intensity. Linear is fine — TRANSITION_MS is
    // long enough that the eye reads it as a slow drift, not a step.
    if (this.intensity !== this.targetIntensity) {
      const step = dt / TRANSITION_MS;
      if (this.intensity < this.targetIntensity) {
        this.intensity = Math.min(this.targetIntensity, this.intensity + step);
      } else {
        this.intensity = Math.max(this.targetIntensity, this.intensity - step);
      }
    }

    // Animate rain layers — alpha scaled by intensity. At intensity 0 the
    // sprites are invisible; we still advance tilePosition so they're in
    // motion the moment the next state arrives.
    for (const layer of this.layers) {
      layer.sprite.tilePositionY += layer.speedY * dt;
      layer.sprite.tilePositionX += layer.driftX * dt;
      const a = layer.peakAlpha * this.intensity;
      layer.sprite.setAlpha(a);
      // Hide entirely when alpha is effectively zero — save a draw call.
      layer.sprite.setVisible(a > 0.005);
    }

    // Animate wisps — slow drift + sinusoidal twinkle. Alpha = base + bonus×i.
    const wispAlphaBase = WISP_BASE_ALPHA + WISP_INTENSITY_BONUS * this.intensity;
    for (const w of this.wisps) {
      let nx = w.img.x + w.vx * dt;
      let ny = w.img.y + w.vy * dt;
      if (nx < -30)      nx = w.ww + 30;
      if (nx > w.ww + 30) nx = -30;
      if (ny < -30)      ny = w.wh + 30;
      if (ny > w.wh + 30) ny = -30;
      w.img.setPosition(nx, ny);
      const twinkle = 0.5 + 0.5 * Math.sin((nowMs + w.phaseMs) / 1000 * w.pulseHz * Math.PI * 2);
      w.img.setAlpha(wispAlphaBase * twinkle);
    }

    // Lightning — only eligible during storm AND when intensity is near
    // its storm target. Schedule next flash lazily when first entering
    // the eligibility window.
    const stormEligible = this.state === 'storm' && this.intensity >= LIGHTNING_INTENSITY_GATE;
    if (stormEligible && this.nextLightningMs === Number.POSITIVE_INFINITY) {
      this.nextLightningMs = nowMs + LIGHTNING_INTERVAL_MIN_MS +
        Math.random() * (LIGHTNING_INTERVAL_MAX_MS - LIGHTNING_INTERVAL_MIN_MS);
    }
    if (!stormEligible) {
      // Reset the schedule whenever we leave the eligibility window so
      // re-entering doesn't trigger a stale flash a moment later.
      this.nextLightningMs = Number.POSITIVE_INFINITY;
      if (this.lightningActive && this.lightning !== null) {
        this.lightning.setAlpha(0);
        this.lightningActive = false;
      }
    }
    if (!this.lightningActive && stormEligible && nowMs > this.nextLightningMs) {
      this.triggerLightning(nowMs);
    }
    if (this.lightningActive && this.lightning !== null) {
      const age = nowMs - this.lightningBornMs;
      if (age >= LIGHTNING_FADE_MS) {
        this.lightning.setAlpha(0);
        this.lightningActive = false;
        this.nextLightningMs =
          nowMs + LIGHTNING_INTERVAL_MIN_MS +
          Math.random() * (LIGHTNING_INTERVAL_MAX_MS - LIGHTNING_INTERVAL_MIN_MS);
      } else {
        const t = age / LIGHTNING_FADE_MS;
        const alpha = LIGHTNING_FLASH_ALPHA * Math.pow(1 - t, 2.4);
        this.lightning.setAlpha(alpha);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // State-machine plumbing
  // ---------------------------------------------------------------------------

  private advanceState(nowMs: number): void {
    const next = this.pickNext(this.state);
    if (next !== this.state) {
      this.state = next;
      this.targetIntensity = STATE_INTENSITY[next];
      this.stateChangedAtMs = nowMs;
    }
    this.nextStateAtMs = this.rollDwell(this.state, nowMs);
  }

  private pickNext(current: WeatherState): WeatherState {
    const table = TRANSITIONS[current];
    let totalWeight = 0;
    for (const [, w] of table) totalWeight += w;
    let roll = Math.random() * totalWeight;
    for (const [state, w] of table) {
      roll -= w;
      if (roll <= 0) return state;
    }
    return current;
  }

  private rollDwell(state: WeatherState, nowMs: number): number {
    const [lo, hi] = DWELL_RANGES[state];
    return nowMs + lo + Math.random() * (hi - lo);
  }

  // ---------------------------------------------------------------------------
  // Rain texture — generated once at boot time with Math.random (client only).
  // ---------------------------------------------------------------------------

  private buildRainTexture(): void {
    if (this.scene.textures.exists(RAIN_TEX_KEY)) return;
    const W = 256, H = 256;
    const tex = this.scene.textures.createCanvas(RAIN_TEX_KEY, W, H);
    if (tex === null) return;
    const ctx = tex.context;
    ctx.clearRect(0, 0, W, H);
    const driftX = Math.sin(RAIN_ANGLE_RAD) * 1;
    ctx.lineCap = 'round';
    for (let i = 0; i < RAIN_STREAK_COUNT; i++) {
      const x = Math.random() * W;
      const y = Math.random() * H;
      const len = 10 + Math.random() * 20;
      const alpha = 0.2 + Math.random() * 0.45;
      const g = Math.round(180 + Math.random() * 60);
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = `rgb(80,${g},255)`;
      ctx.lineWidth = 0.8 + Math.random() * 0.8;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + driftX * len, y + len);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    tex.refresh();
  }

  // ---------------------------------------------------------------------------
  // Rain TileSprite layers (parallax)
  // ---------------------------------------------------------------------------

  private buildRainLayers(map: MapDef): void {
    if (!this.scene.textures.exists(RAIN_TEX_KEY)) return;
    for (let i = 0; i < RAIN_LAYERS; i++) {
      const sp = this.scene.add.tileSprite(0, 0, map.width, map.height, RAIN_TEX_KEY);
      sp.setOrigin(0, 0);
      sp.setDepth(900 + i);
      sp.setAlpha(0);                              // start invisible
      sp.setVisible(false);
      sp.setBlendMode(Phaser.BlendModes.ADD);
      sp.setTileScale(RAIN_SCALES[i] ?? 1);
      sp.tilePositionX = Math.random() * 256;
      sp.tilePositionY = Math.random() * 256;
      this.layers.push({
        sprite: sp,
        speedY: (RAIN_SPEEDS[i] ?? 200) / 1000,
        driftX: (RAIN_X_DRIFT[i] ?? 0.1),
        peakAlpha: RAIN_PEAK_ALPHAS[i] ?? 0.1,
      });
      this.allObjs.push(sp);
    }
  }

  // ---------------------------------------------------------------------------
  // Lightning flash — full-arena screen-wide white rectangle
  // ---------------------------------------------------------------------------

  private buildLightningFlash(map: MapDef): void {
    const g = this.scene.add.graphics();
    g.setDepth(950);
    g.fillStyle(0xd8eeff, 1);
    g.fillRect(-100, -100, map.width + 200, map.height + 200);
    g.setAlpha(0);
    this.lightning = g;
    this.allObjs.push(g);
  }

  private triggerLightning(nowMs: number): void {
    this.lightningActive = true;
    this.lightningBornMs = nowMs;
    this.lightning?.setAlpha(LIGHTNING_FLASH_ALPHA);
  }

  // ---------------------------------------------------------------------------
  // Ambient electrical wisps (reuse fx atlas)
  // ---------------------------------------------------------------------------

  private buildWisps(map: MapDef): void {
    if (!this.scene.textures.exists(FX_KEY)) return;
    const frames = ['spark', 'dust_mote'];
    for (let i = 0; i < WISP_COUNT; i++) {
      const frame = frames[i % 2] ?? 'spark';
      const img = this.scene.add.image(
        Math.random() * map.width,
        Math.random() * map.height,
        FX_KEY,
        frame,
      );
      img.setOrigin(0.5, 0.5);
      img.setDepth(910);
      img.setBlendMode(Phaser.BlendModes.ADD);
      const sz = 8 + Math.random() * 16;
      img.setDisplaySize(sz, sz);
      img.setTint(i % 3 === 0 ? 0x80c8ff : 0x50e0c0);
      img.setAlpha(WISP_BASE_ALPHA);
      const speed = 6 + Math.random() * 12;
      const angle = Math.random() * Math.PI * 2;
      this.wisps.push({
        img,
        vx: Math.cos(angle) * speed / 1000,
        vy: Math.sin(angle) * speed / 1000,
        phaseMs: Math.random() * 5000,
        pulseHz: 0.3 + Math.random() * 0.5,
        ww: map.width,
        wh: map.height,
      });
      this.allObjs.push(img);
    }
  }
}
